import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import { formatFileSize } from "../../features/materials/utils/formatFileSize";
import { MaterialUploader } from "../../features/materials/contracts/MaterialUploader";
import { PortalMaterialResponse } from "../../features/portal/contracts/PortalMaterialResponse";
import { downloadPortalMaterial } from "../../features/portal/slice-extensions/portal-slice-extension";
import { fileExtensionLabel, portalMaterialKind } from "../../features/portal/utils/portalMaterialGroups";
import ReportButton from "../messages/ReportButton";
import PortalMaterialViewer from "./PortalMaterialViewer";

interface PortalMaterialRowProps {
  memberId: string;
  material: PortalMaterialResponse;
  canDelete: boolean;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onDelete: () => void;
}

/** One hairline row: name, a short grey type label, Open when viewable, Download always. */
const PortalMaterialRow = ({ memberId, material, canDelete, isOpen, onOpen, onClose, onDelete }: PortalMaterialRowProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const kind = material.viewable ? portalMaterialKind(material.contentType) : null;
  const typeLabel =
    kind === "pdf"
      ? t("portal.library.typePdf")
      : kind === "image"
        ? t("portal.library.typeImage")
        : kind === "audio"
          ? t("portal.library.typeAudio")
          : kind === "video"
            ? t("portal.library.video")
            : fileExtensionLabel(material.fileName);
  const uploader =
    material.uploadedBy === MaterialUploader.Member
      ? material.uploadedByFirstName ?? t("portal.materials.uploaderMember")
      : t("portal.materials.uploaderInstructor");

  return (
    <li className="py-3 text-sm" data-testid="portal-material-row">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-grey-20 dark:text-white">{material.fileName}</p>
          <p className="truncate text-xs text-grey-40 dark:text-grey-80">
            {[typeLabel, material.category, formatFileSize(material.sizeBytes), uploader].filter(Boolean).join(" · ")}
          </p>
        </div>
        {kind && (
          <button type="button" className="text-sm text-primary-40 hover:underline dark:text-primary-90" onClick={isOpen ? onClose : onOpen}>
            {t("portal.library.open")}
          </button>
        )}
        <button
          type="button"
          className="text-sm text-grey-30 hover:underline dark:text-grey-80"
          onClick={() => dispatch(downloadPortalMaterial({ memberId, materialId: material.id }))}
        >
          {t("portal.library.download")}
        </button>
        {!canDelete && <ReportButton face="member" target={{ targetKind: "Material", targetId: material.id }} />}
        {canDelete && (
          <button type="button" className="btn btn-secondary" onClick={onDelete} aria-label={t("portal.materials.removeAction")}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {isOpen && kind && <PortalMaterialViewer memberId={memberId} material={material} onClose={onClose} />}
    </li>
  );
};

export default PortalMaterialRow;
